import {
  ChangeEvent, FC, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Modal,
  Tooltip,
  Spin,
  Checkbox,
  Icons,
  Form,
  Input,
  Button,
  Upload,
  Image,
  Typography,
  Radio,
} from 'taxaroo-ui';
import { ConditionalEntity, TranslationEntity } from '~src/graphql';
import { useAppSelector } from '~src/redux/hooks';
import {
  useDownloadFileFromS3Mutation,
  useGetPresignedUrlMutation,
} from '~src/graphql/mutations/clients';
import { isAllowedFileType } from '~src/components/helpers/fileTypes';
import axios from 'axios';
import { handleOnErrorDownloadFile } from '~src/components/atoms/FileNameClickableDownload/utils';
import {
  handleFileExceedsSize,
  handleOnFileTypeNotSupported,
  handleOnGetPresignedURLS3Error,
} from '../Client/Jobs/Job/ClientFiles/utils';
import style from './style.module.css';

const { Text } = Typography;

const { InfoCircleOutlined, DeleteOutlined, PlusOutlined } = Icons;

const UploadButton = () => (
  <button style={{ border: 0, background: 'none' }} type="button">
    <PlusOutlined />
    <div style={{ marginTop: 8 }}>Upload</div>
  </button>
);

export interface UiOption {
  value: string;
  label: string;
  checked: boolean;
}

interface ConditionalModalProps {
  visible: boolean;
  loading: boolean;
  isEditing: boolean;
  onCancel: () => void;
  options: Array<UiOption>;
  isSubsection?: boolean;
  onOk: (result: { selectedOptions: Array<UiOption> }) => void;
  onOkCreateQuestion: (result: {
    questionTitle: string;
    optionToEnable: string;
    picture?: string;
    hintText?: string;
    questionId?: string;
  }) => void;
  conditional: ConditionalEntity;
  interviewId: string;
  sectionId: string;
  onDeleteConditional: (data: { questionId: string; conditionalId: string }) => void;
}

const ConditionalModal: FC<ConditionalModalProps> = ({
  visible,
  options,
  isEditing,
  onCancel,
  onOk,
  loading,
  isSubsection = false,
  onOkCreateQuestion,
  conditional,
  interviewId,
  sectionId,
  onDeleteConditional,
}) => {
  const initialRender = useRef<boolean>(true);
  const { languageId: currentLanguageId } = useAppSelector((state) => state.session);
  const [initialOptions, setInitialOptions] = useState<Array<UiOption>>(options);
  const [optionCheckboxes, setOptionCheckboxes] = useState<Array<UiOption>>(options);
  const [form] = Form.useForm();
  const [conditionalImage, setConditionalImage] = useState<any>(null);
  const [conditionalImageUrl, setConditionalImageUrl] = useState<string | null>(null);
  const [getPresignedUrl, { data: uploadFileData }] = useGetPresignedUrlMutation({
    onError: handleOnGetPresignedURLS3Error,
  });
  const [downloadFile] = useDownloadFileFromS3Mutation({
    onError: handleOnErrorDownloadFile,
  });

  useEffect(() => {
    console.log(sectionId, interviewId);
  }, [interviewId, sectionId]);

  const getTitle = (translations: TranslationEntity[]) => {
    const translation = translations.find((t) => t.languageId === currentLanguageId);
    return translation?.description;
  };

  useEffect(() => {
    if (conditional?.picture) {
      downloadFile({
        variables: {
          s3Key: conditional?.picture,
        },
        onCompleted: (data) => {
          setConditionalImageUrl(data.downloadFile.signedUrl);
        },
      });
    }
  }, [conditional]);

  useEffect(() => {
    console.log('conditionalImageUrl', conditionalImageUrl);
  }, [conditionalImageUrl]);

  const intiialValues = useMemo(() => {
    if (conditional) {
      setConditionalImage(conditional.picture);
      const selectedOptionTranslation = conditional.Options.Questions.Options.find(
        (option) => option.id === conditional.optionId,
      ).Translations.find((t) => t.Language.lang === 'EN');
      console.log(selectedOptionTranslation);
      return {
        questionTitle: getTitle(conditional.Options.Questions.Translations),
        optionToEnable: selectedOptionTranslation?.description.toLowerCase(),
        hint: conditional.hintText,
        // options: conditional.Options.Questions.Options.map((option) => ({
        //   name: getTitle(option.Translations),
        //   id: option.id,
        // })),
      };
    }

    return {};
  }, [conditional]);

  const uploadConditionalImage = async (file: File) => {
    try {
      const key = `conditionals/${interviewId}/${sectionId}/${file.name}`;
      const { data: conditionalUploadData } = await getPresignedUrl({
        variables: {
          fileName: key,
          fileType: file.type,
        },
      });
      if (conditionalUploadData.generatePreSignedPutUrl) {
        const { signedUrl, url } = conditionalUploadData.generatePreSignedPutUrl;
        const fileUrl = new URL(signedUrl);
        await axios.put(fileUrl.toString(), file, {
          headers: {
            'Content-Type': 'image/png',
          },
        });
        return url.split('.com/')[1];
      }

      return undefined;
    } catch (error) {
      console.log('error', error);
      return undefined;
    }
  };

  const handleOnOk = async () => {
    if (isSubsection) {
      if (!isEditing) {
        onOk({ selectedOptions: optionCheckboxes.filter(({ checked }) => checked) });
        setInitialOptions([]);
      } else {
        onOk({
          selectedOptions: optionCheckboxes.filter(({ value, checked }) => {
            const currentOption = initialOptions.find(({ value: optionId }) => optionId === value);

            return !!currentOption && currentOption.checked !== checked;
          }),
        });
        setInitialOptions([]);
      }
    } else {
      const valid = await form.validateFields();
      if (valid) {
        const { questionTitle, optionToEnable, hint } = form.getFieldsValue();
        console.log('conditional image!', conditionalImage);
        const imageUploadResult = conditionalImage
          ? await uploadConditionalImage(conditionalImage)
          : null;
        console.log(imageUploadResult);
        if (conditionalImage && imageUploadResult) {
          onOkCreateQuestion({
            questionTitle,
            optionToEnable,
            picture: imageUploadResult,
            hintText: hint,
            questionId: conditional?.Options.questionId || null,
          });
        }
      }
    }
  };

  const handleSelectedOptionsChange = (event: ChangeEvent) => {
    setOptionCheckboxes((previous) => previous.map((option) => {
      if (event.target.id === option.value) {
        return {
          ...option,
          checked: !option.checked,
        };
      }

      return option;
    }));
  };

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    }

    if (!initialRender.current && !initialOptions.length) {
      setInitialOptions(options);
    }
    setOptionCheckboxes(options);
    form.resetFields();
  }, [options]);

  /// ///////////////////////

  // const [submittable, setSubmittable] = React.useState<boolean>(false);

  // // Watch all values
  // const values = Form.useWatch([], form);

  // useEffect(() => {
  //   form
  //     .validateFields({ validateOnly: true })
  //     .then(() => setSubmittable(true))
  //     .catch(() => setSubmittable(false));
  // }, [form, values]);

  /// ///////////////////////

  const handleDeleteConditional = () => {
    onDeleteConditional({
      questionId: conditional.Options.questionId,
      conditionalId: conditional.id,
    });
  };

  const beforeUpload = (file: File) => {
    const maxSize = 1024 * 1024 * 25; // 25MB

    if (file.size > maxSize) {
      handleFileExceedsSize();
      return false;
    }

    if (!isAllowedFileType(file.type)) {
      handleOnFileTypeNotSupported();
      return false;
    }
    return false;
  };

  const handleChange = (info: any) => {
    setConditionalImage(info.file);
  };

  return visible ? (
    <Modal
      open={visible}
      onCancel={onCancel}
      okText="Save"
      okButtonProps={{
        disabled: isSubsection ? !optionCheckboxes.length : false,
      }}
      onOk={handleOnOk}
      footer={[
        conditional && (
          <Button color="red" key="delete" onClick={handleDeleteConditional}>
            Delete
          </Button>
        ),
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleOnOk}>
          Save
        </Button>,
      ]}
      title={(
        <div className={style.modalHeader}>
          <h2>
            {`${isEditing ? 'Update' : 'Create'}`}
            {' '}
            Conditionals
          </h2>
          <Tooltip
            placement="bottom"
            title="Consists of selecting the options of the questions of the parent section with which you want this sub-section to be active/visible to the client."
          >
            <InfoCircleOutlined size="small" />
          </Tooltip>
        </div>
      )}
    >
      <div className={style.modalContentContainer}>
        {loading && <Spin />}
        {isSubsection && (
          <>
            <div className={style.selectContainer}>
              <strong>
                {optionCheckboxes.length
                  ? 'Check the desired options to condition this section'
                  : 'There is no options to condition this section'}
              </strong>
              <div className={style.checkboxContainer}>
                {optionCheckboxes.map(({ label, checked, value }) => (
                  <Checkbox
                    id={value}
                    checked={checked}
                    onChange={handleSelectedOptionsChange}
                    Tooltip
                  >
                    {label}
                  </Checkbox>
                ))}
              </div>
            </div>
            <div className={style.selectContainer} />
          </>
        )}
        {!isSubsection && (
          <Form form={form} layout="vertical" initialValues={intiialValues}>
            <Form.Item
              name="questionTitle"
              label="Question title"
              rules={[{ required: true, message: 'Add a question title' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="optionToEnable"
              label="Option to enable"
              rules={[{ required: true, message: 'Select an option' }]}
            >
              <Radio.Group>
                <Radio value="yes">Yes</Radio>
                <Radio value="no">No</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item name="conditionalPhoto" label="Conditional picture">
              <Upload
                name="conditionalPhoto"
                listType="picture-card"
                accept="image/png, image/jpeg"
                beforeUpload={beforeUpload}
                onChange={handleChange}
                onRemove={() => setConditionalImage(null)}
              >
                {conditional?.picture ? (
                  <Image src={conditionalImageUrl} />
                ) : (
                  !conditionalImage && <UploadButton />
                )}
              </Upload>
              {/* <Text type="secondary">Max file size: 5MB</Text> */}
            </Form.Item>
            <Form.Item name="hint" label="Conditional Hint">
              <Input placeholder="Add a hint to help users" />
            </Form.Item>
          </Form>
        )}
      </div>
    </Modal>
  ) : null;
};

ConditionalModal.defaultProps = {
  isSubsection: false,
};

export default ConditionalModal;
